// utils/urlUtils.js
import { createMultilingualSlug, getLocalizedText } from './multilingualUtils';

/**
 * Create a URL-friendly slug from text
 * @param {string} text - Text to convert to slug
 * @returns {string} URL-friendly slug
 */
export const createSlug = (text) => {
    if (!text) return '';

    return text
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generate product URL based on SKU or name (always uses English for consistent URLs)
 * @param {object} product - Product object
 * @param {object} selectedVariant - Optional selected variant
 * @returns {string} Product URL
 */
export const generateProductUrl = (product, selectedVariant = null) => {
    if (!product) return '/';

    let slug = '';

    // Priority: Product Name (English) > SKU > Product ID
    // Always use English for URL consistency regardless of current language
    if (product.name) {
        const englishName = getLocalizedText(product.name, 'en', 'en');
        if (englishName && englishName.trim()) {
            slug = createMultilingualSlug(product.name);
        }
    }

    // Fallback to SKU if no name slug
    if (!slug && product.sku && product.sku.trim()) {
        slug = createSlug(product.sku);
    }

    // Final fallback to product ID
    if (!slug) {
        slug = product.id;
    }

    // Build base URL
    let url = `/product/${slug}`;

    // Add variant information if provided
    if (selectedVariant && selectedVariant.id) {
        const variantParams = new URLSearchParams();

        // Add variant ID
        variantParams.set('variant', selectedVariant.id);

        // Add variant options for better SEO
        Object.keys(selectedVariant).forEach(key => {
            if (!['id', 'price', 'quantity', 'sku', 'images'].includes(key) &&
                selectedVariant[key] !== null &&
                selectedVariant[key] !== undefined) {

                let value = selectedVariant[key];

                // Handle multilingual values uniformly - extract English for URL
                try {
                    const valueData = JSON.parse(value);
                    if (typeof valueData === 'object' && valueData !== null) {
                        value = valueData.en || Object.values(valueData)[0] || value;
                    }
                } catch {
                    // Use value as-is if not JSON
                }

                variantParams.set(key, value);
            }
        });

        url += `?${variantParams.toString()}`;
    }

    return url;
};

/**
 * Parse product slug and return search criteria
 * @param {string} productSlug - URL slug from params
 * @returns {object} Search criteria for finding product
 */
export const parseProductSlug = (productSlug) => {
    // console.log("🔍 parseProductSlug input:", productSlug);

    if (!productSlug) return null;

    const decodedSlug = decodeURIComponent(productSlug);
    // console.log("🔍 Decoded slug:", decodedSlug);

    // Remove any trailing parameters or fragments
    const cleanSlug = decodedSlug.split('?')[0].split('#')[0];
    // console.log("🔍 Clean slug:", cleanSlug);

    // Check if it's a direct product ID (Firebase IDs are typically 20+ chars, alphanumeric only)
    if (cleanSlug.length >= 20 && /^[a-zA-Z0-9]+$/.test(cleanSlug) && !cleanSlug.includes('-')) {
        // console.log("🔍 Detected as Firebase ID");
        return {
            type: 'id',
            value: cleanSlug,
            searchBy: 'id'
        };
    }

    // For shorter strings or strings with hyphens, prioritize name-based search
    // This handles product names like "brown-abaya", "red-dress", etc.
    // console.log("🔍 Detected as name slug");
    return {
        type: 'name',
        value: cleanSlug.toLowerCase(),
        searchBy: 'name'
    };
};

/**
 * Parse URL search params for variant information
 * @param {string} search - URL search string
 * @returns {object} Variant information from URL
 */
export const parseVariantFromUrl = (search) => {
    if (!search) return null;

    const params = new URLSearchParams(search);
    const variantInfo = {};

    // Get variant ID if present
    const variantId = params.get('variant');
    if (variantId) {
        variantInfo.id = variantId;
    }

    // Get variant options
    const variantOptions = {};
    params.forEach((value, key) => {
        if (key !== 'variant') {
            variantOptions[key] = value;
        }
    });

    if (Object.keys(variantOptions).length > 0) {
        variantInfo.options = variantOptions;
    }

    return Object.keys(variantInfo).length > 0 ? variantInfo : null;
};

/**
 * Update URL without page reload
 * @param {string} newUrl - New URL to navigate to
 * @param {boolean} replace - Whether to replace current history entry
 */
export const updateUrl = (newUrl, replace = false) => {
    if (replace) {
        window.history.replaceState({}, '', newUrl);
    } else {
        window.history.pushState({}, '', newUrl);
    }
};

/**
 * Generate SEO-friendly page title with multilingual support
 * @param {object} product - Product object
 * @param {object} selectedVariant - Selected variant
 * @param {string} language - Current language
 * @returns {string} Page title
 */
export const generatePageTitle = (product, selectedVariant = null, language = 'en') => {
    if (!product) return 'Product Not Found';

    // Use multilingual product name
    let title = getLocalizedText(product.name, language);

    if (selectedVariant) {
        // Import here to avoid circular dependency
        const { createLocalizedDisplayVariantText } = require('./multilingualUtils');
        const variantText = createLocalizedDisplayVariantText(product, selectedVariant, language);

        if (variantText) {
            title += ` - ${variantText}`;
        }
    }

    return title;
};
